import { useId } from 'react';
import type { ReactNode } from 'react';

interface BaseProps {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children?: ReactNode;
}

/** Wraps any control with the printed-form label, hint and error line. */
export function Field({ label, error, hint, required, children }: BaseProps) {
  return (
    <label className="block">
      <span className="field-label">
        {label}
        {required && <span className="text-[color:var(--color-flag)]"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-[color:var(--color-ink-soft)]">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

interface TextFieldProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel' | 'password' | 'date' | 'datetime-local' | 'number';
  placeholder?: string;
  autoComplete?: string;
  min?: string | number;
  max?: string | number;
  disabled?: boolean;
  /**
   * A value the form needs but the person may not change. Not `disabled`: a
   * disabled input is skipped by the tab order and read out as unavailable,
   * where this is a real value they should be able to select and check.
   */
  readOnly?: boolean;
}

export function TextField({
  label, value, onChange, error, hint, required, type = 'text',
  placeholder, autoComplete, min, max, disabled, readOnly,
}: TextFieldProps) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <input
        className="field-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : undefined}
        required={required}
        min={min}
        max={max}
        disabled={disabled}
        readOnly={readOnly}
      />
    </Field>
  );
}

interface TextAreaProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
}

export function TextArea({ label, value, onChange, error, hint, required, rows = 3, placeholder }: TextAreaProps) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <textarea
        className="field-textarea"
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : undefined}
        required={required}
      />
    </Field>
  );
}

interface SelectProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

export function SelectField({ label, value, onChange, options, error, hint, required }: SelectProps) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <select className="field-select" value={value} onChange={(e) => onChange(e.target.value)} required={required}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  /** Off and not turnable on. The hint has to say why, or this is just broken. */
  disabled?: boolean;
}

/** A checkbox styled as a form tick box, for the many yes/no intake fields. */
export function Toggle({ label, checked, onChange, hint, disabled }: ToggleProps) {
  const id = useId();
  return (
    <div className={`flex items-start gap-2.5 py-1 ${disabled ? 'opacity-60' : ''}`}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 accent-[color:var(--color-stamp)] disabled:cursor-not-allowed"
      />
      <label
        htmlFor={id}
        className={`text-sm leading-snug ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {label}
        {hint && <span className="block text-xs text-[color:var(--color-ink-soft)]">{hint}</span>}
      </label>
    </div>
  );
}

interface YesNoProps {
  label: string;
  value: 'yes' | 'no' | 'na';
  onChange: (value: 'yes' | 'no' | 'na') => void;
  allowNa?: boolean;
}

/** The Y / N boxes from the paper form, as a small radio group. */
export function YesNo({ label, value, onChange, allowNa = true }: YesNoProps) {
  const options: { value: 'yes' | 'no' | 'na'; label: string }[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    ...(allowNa ? [{ value: 'na' as const, label: 'N/A' }] : []),
  ];

  return (
    <fieldset className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--color-rule)] py-2">
      <legend className="sr-only">{label}</legend>
      <span className="text-sm">{label}</span>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            onClick={() => onChange(option.value)}
            className={`min-w-11 rounded-sm border px-2.5 py-1 font-mono text-xs tracking-wider uppercase transition-colors ${
              value === option.value
                ? 'border-[color:var(--color-stamp)] bg-[color:var(--color-stamp)] text-white'
                : 'border-[color:var(--color-rule)] text-[color:var(--color-ink-soft)] hover:border-[color:var(--color-ink)]'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}
